import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';
import { SESSION_EXPIRED_EVENT, getAuthToken } from './api/services/authService';
import { AuthProvider } from './context/AuthContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AuthErrorBoundary from './components/AuthErrorBoundary/AuthErrorBoundary';
import Landing from './pages/Landing/Landing';
import Login from './pages/Login/Login';
import { onForegroundMessage, registerFcmToken } from './api/services/fcmService';
import SignUp from './pages/SignUp/SignUp';
import VerifyOTP from './pages/VerifyOTP/VerifyOTP';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import LandlordDashboard from './pages/Landlord/LandlordDashboard';
import AirbnbHostDashboard from './pages/AirbnbHost/AirbnbHostDashboard';
import AgentDashboard from './pages/Agent/AgentDashboard';
import SystemAdminDashboard from './pages/SystemAdmin/SystemAdminDashboard';
import TenantDashboard from './pages/Tenant/TenantDashboard';
import Forbidden from './pages/Forbidden/Forbidden';
import PublicListings from './pages/PublicListings/PublicListings';
import SubscriptionPlans from './pages/Subscription/SubscriptionPlans';
import DeleteAccountPage from './pages/Profile/DeleteAccountPage';

import PublicPropertyDetails from './pages/Landing/components/PublicPropertyDetails';
import PrivacyNotice from './pages/Legal/PrivacyNotice';
import TermsOfService from './pages/Legal/TermsOfService';
import CookiesPolicy from './pages/Legal/CookiesPolicy';
import CookieConsentBanner from './components/CookieConsentBanner/CookieConsentBanner';
import Blog from './pages/Blog/Blog';
import ScreenTenantsKenya from './pages/Blog/ScreenTenantsKenya';
import ShareRedirect from './pages/shared/ShareRedirect';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker/AnalyticsRouteTracker';
import ToastNotification, { showToastNotification } from './components/ToastNotification/ToastNotification';

/** Listens for session expiry (e.g. bearer token expired) and redirects to login on all dashboards */
function SessionExpiryHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const handleSessionExpired = () => {
      navigate('/login', { replace: true });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [navigate]);
  return null;
}

/** Listens for foreground push notifications (when app is active) */
function ForegroundNotificationHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Proactively register token if user is authenticated (fixes session persistence issues)
    const token = getAuthToken();
    if (token) {
      registerFcmToken().catch(err => console.error('[FCM] Startup registration failed:', err));
    }

    // This will run throughout the app's lifecycle
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('🔔 Foreground Notification received:', payload);

      const { title, body } = payload.notification || {};
      const { threadId } = payload.data || {};

      // Broadcast custom event so active screens (like Messages/Notifications) can immediately refresh
      window.dispatchEvent(new CustomEvent('app:new_notification', { detail: payload }));

      // Basic browser notification for foreground
      if (Notification.permission === 'granted' && title) {
        const notification = new Notification(title, {
          body: body || '',
          icon: '/android-chrome-512x512.png',
          data: payload.data,
          tag: threadId || 'general'
        });

        notification.onclick = (e) => {
          e.preventDefault();
          window.focus();
          if (threadId) {
            // Navigate to messages
            navigate('/tenant/messages', { state: { threadId } });
          }
          notification.close();
        };
      } else if (title) {
        // Non-blocking in-app toast banner
        showToastNotification({
          title,
          body,
          threadId,
          onClick: () => {
            if (threadId) navigate('/tenant/messages', { state: { threadId } });
          }
        });
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [navigate]);

  return <ToastNotification onNavigate={navigate} />;
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || '875852320343-v16eer5veaahg6r7r5uclcem4rg5hovl.apps.googleusercontent.com'}>
    <Router>
      <AuthProvider>
      <CookieConsentProvider>
      <ScrollToTop />
      <AnalyticsRouteTracker />
      <SessionExpiryHandler />
      <ForegroundNotificationHandler />
      <CookieConsentBanner />
      <div className="app">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/verify-otp" element={<VerifyOTP />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/landlord/deleteaccount"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['LANDLORD']}>
                  <DeleteAccountPage />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/landlord"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['LANDLORD']}>
                  <LandlordDashboard />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/airbnb-host/deleteaccount"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['BNB_HOST']}>
                  <DeleteAccountPage />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/airbnb-host"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['BNB_HOST']}>
                  <AirbnbHostDashboard />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route path="/subscription-plans" element={<SubscriptionPlans />} />
          <Route
            path="/agent"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['AGENT']}>
                  <AgentDashboard />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/agent/deleteaccount"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['AGENT']}>
                  <DeleteAccountPage />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/airbnb-host/deleteaccount"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['BNB_HOST']}>
                  <DeleteAccountPage />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/system-admin/*"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <SystemAdminDashboard />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/tenant/deleteaccount"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['TENANT', 'GUEST']}>
                  <DeleteAccountPage />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route
            path="/tenant/*"
            element={
              <AuthErrorBoundary>
                <ProtectedRoute allowedRoles={['TENANT', 'GUEST']}>
                  <TenantDashboard />
                </ProtectedRoute>
              </AuthErrorBoundary>
            }
          />
          <Route path="/property/:id" element={<PublicPropertyDetails />} />
          <Route path="/listings" element={<PublicListings />} />
          <Route path="/privacy" element={<PrivacyNotice />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/screen-tenants-kenya" element={<ScreenTenantsKenya />} />
          <Route path="/forbidden" element={<Forbidden />} />
          
          {/* Shared Links Redirection */}
          <Route path="/s/p/:id" element={<ShareRedirect type="property" />} />
          <Route path="/s/u/:id" element={<ShareRedirect type="unit" />} />
        </Routes>
      </div>
      </CookieConsentProvider>
      </AuthProvider>
    </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
